import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppInsightsContext,
  EntityCardSize,
  MetadataContext,
  ModuleBase,
  TrackEventKey,
  TrackEventName,
  TrackEventType,
  UserContext,
} from '@/components';
import { ModuleBaseModel, ModuleBasePropsModel, ReportSearchModel, TaxonomyModel } from '@/types';
import ReportCard from '@/components/report/ReportCard';
import { useRouter } from 'next/router';
import XLight from '@/assets/xLight.svg';
import classNames from 'classnames';
import { assignValuesFromRouteQuery, pushValuesToRouteQuery } from '@/utils';

type TaxonomyMapping = {
  map: {
    directories: TaxonomyModel[];
    innovationPlatforms: TaxonomyModel[];
    reportId: number;
  }[];
};

type LookAheadLandingPageModuleModel = ModuleBaseModel<
  {
    directories: TaxonomyModel[];
    pillarsOfInnovation: TaxonomyModel[];
    itemName: string;
    reports: ReportSearchModel[];
    taxonomyMapping: TaxonomyMapping;
  } & ModuleBasePropsModel
>;

const LookAheadLandingPageModule = (data: LookAheadLandingPageModuleModel) => {
  const {
    props: { directories, pillarsOfInnovation, reports, taxonomyMapping },
  } = data;

  const appInsights = useContext(AppInsightsContext);
  const user = useContext(UserContext);
  const { pageId } = useContext(MetadataContext);
  const router = useRouter();
  const [filteredReports, setFilteredReports] = useState<ReportSearchModel[]>(reports);
  const [mergedFilters, setMergedFilters] = useState<string[]>([]);
  const activeFilters = useRef<{ [key: string]: string[] }>({});
  const filterTimer = useRef<NodeJS.Timeout | null>(null);

  const filters = useMemo(
    () => [...(directories || []), ...(pillarsOfInnovation || [])],
    [directories, pillarsOfInnovation],
  );

  useEffect(() => {
    if (directories) {
      activeFilters.current['directories'] = [];
    }
    if (pillarsOfInnovation) {
      activeFilters.current['innovation-platforms'] = [];
    }
    readFiltersFromRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergedFilters]);

  const toggleSubscription = (filter: TaxonomyModel) => {
    if (!activeFilters.current[filter.group]) {
      activeFilters.current[filter.group] = [];
    }

    if (activeFilters.current[filter.group].includes(filter.id)) {
      activeFilters.current[filter.group].splice(activeFilters.current[filter.group].indexOf(filter.id), 1);
    } else {
      activeFilters.current[filter.group].push(filter.id);
    }

    pushValuesToRouteQuery(router, activeFilters.current);
    buildMergedFilters();
  };

  const buildMergedFilters = () => {
    setMergedFilters([
      ...(activeFilters.current.directories || []),
      ...(activeFilters.current['innovation-platforms'] || []),
    ]);
  };

  const readFiltersFromRoute = () => {
    assignValuesFromRouteQuery(router, activeFilters.current);

    setTimeout(() => {
      buildMergedFilters();
    }, 0);
  };

  const filterReports = () => {
    setFilteredReports(
      filteredReports.map((report) => {
        return {
          ...report,
          isLoading: true,
        };
      }),
    );
    clearTimeout(filterTimer.current!);
    filterTimer.current = setTimeout(() => {
      if (mergedFilters.length > 0) {
        const filteredReportIds: string[] = [];
        taxonomyMapping?.map?.forEach((element) => {
          const groups = [...element.directories, ...element.innovationPlatforms];
          groups.forEach((group) => {
            if (mergedFilters.includes(group.id)) {
              filteredReportIds.push(element.reportId + '');
            }
          });
        });

        setFilteredReports(reports.filter((report) => filteredReportIds.includes(report.reportId + '')));
      } else {
        setFilteredReports([...reports]);
      }
    }, 500);
  };

  const onReportCardClick = (e: React.MouseEvent, data: ReportSearchModel) => {
    appInsights.trackCustomEvent(TrackEventName.PAGE, TrackEventKey.LOOK_AHEAD, TrackEventType.SELECTED, user, {
      pageId,
      pagePath: router.asPath,
      reportID: data.reportId,
    });
  };

  return (
    <ModuleBase data={data} noPadding>
      <div className="container flex flex-col gap-4 pb-16">
        <div className="flex flex-wrap gap-2 pt-5">
          {filters.map((filter) => (
            <button
              key={filter.id}
              className={classNames(
                'flex cursor-pointer items-center gap-1.5 rounded-full border border-black py-2 text-base font-medium transition-colors duration-200',
                mergedFilters.includes(filter.id)
                  ? 'bg-black px-3 text-white hover:bg-gray-800'
                  : 'bg-white px-[23px] text-gray-700 hover:bg-gray-100',
              )}
              onClick={() => toggleSubscription(filter)}
            >
              {filter.name}
              {mergedFilters.includes(filter.id) && (
                <XLight className={classNames('h-4 w-4 fill-white opacity-100 transition-all duration-200')} />
              )}
            </button>
          ))}
        </div>

        <div className="flex flex-col">
          <p className="text-h2 mt-10 mb-4">Trends that shape the year ahead</p>
          <div className="h-px w-full bg-black/10" />
        </div>

        <div className="grid w-full grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4">
          {filteredReports.map((report) => (
            <ReportCard
              className="[&_.report-card>div]:!p-2 [&_.report-card>div]:!text-[10px]"
              key={report.reportId}
              size={EntityCardSize.AUTO}
              data={report}
              onClick={onReportCardClick}
            />
          ))}
        </div>
      </div>
    </ModuleBase>
  );
};

export default LookAheadLandingPageModule;
